import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDownLeft, ArrowUpRight, BriefcaseBusiness, CheckCircle2, ChevronRight, CircleDollarSign, Clock3, Landmark, Sparkles, Wallet, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { track } from '../../services/measurement';
import { useAuth } from '../auth/AuthContext';

import {
  confirmCreatorWithdrawal,
  getCreatorWallet,
  quoteCreatorWithdrawal,
  retryCreatorWithdrawal,
  startCreatorGlobalPayoutsOnboarding,
} from '../../services/api';
import Button from '../common/design-system/Button';
import styles from './CreatorWallet.module.scss';

const money = (value, currency) => new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: currency || 'USD'
}).format(Number(value || 0));

const onboardingErrorMessage = (error, t) => {
  if (error?.response?.data?.code === 'stripe_global_payouts_conflict') {
    return t('wallet.errors.configuration_conflict');
  }
  if (error?.response?.data?.code === 'stripe_global_payouts_validation_error') {
    return t('wallet.errors.country_configuration');
  }
  return t('wallet.errors.onboarding');
};

const withdrawalErrorMessage = (error, t) => {
  const code = error?.response?.data?.code;
  const messages = {
    payout_setup_required: 'wallet.withdrawal.setup_required',
    payout_under_review: 'wallet.withdrawal.under_review',
    payout_method_missing: 'wallet.withdrawal.method_missing',
    no_available_balance: 'wallet.withdrawal.no_available',
    withdrawal_already_processing: 'wallet.withdrawal.already_processing',
    quote_expired: 'wallet.withdrawal.quote_expired',
    partial_withdrawal_unavailable: 'wallet.withdrawal.partial_unavailable',
    payout_amount_too_small: 'wallet.withdrawal.amount_too_small',
    PAYOUT_AMOUNT_TOO_SMALL: 'wallet.withdrawal.amount_too_small',
    PAYOUT_PRICING_UNAVAILABLE: 'wallet.withdrawal.quote_error',
    creator_withdrawal_error: 'wallet.withdrawal.quote_error',
    creator_withdrawal_confirmation_error: 'wallet.errors.withdrawal',
    global_payout_precondition_failed: 'wallet.withdrawal.setup_required',
    stripe_quote_error: 'wallet.withdrawal.quote_error',
    stripe_outbound_payment_error: 'wallet.errors.withdrawal',
    quote_invalidated: 'wallet.withdrawal.quote_invalidated',
    withdrawal_not_confirmable: 'wallet.withdrawal.quote_invalidated',
    stripe_global_payouts_error: 'wallet.withdrawal.outbound_error',
    stripe_outbound_payment_uncertain: 'wallet.withdrawal.outcome_pending',
    stripe_payout_method_invalid: 'wallet.withdrawal.method_invalid',
  };
  if (code && messages[code]) {
    return t(messages[code]);
  }
  const detail = error?.response?.data?.error;
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }
  return t('wallet.errors.withdrawal');
};

const CreatorWallet = () => {
  const { t } = useTranslation('creator');
  const { currentUser } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const invitedOfferIds = currentUser?.creator_profile?.invited_offer_ids || [];
  const isApprovedCreator = currentUser?.creator_profile?.admin_verified
    && currentUser?.creator_profile?.verification_status === 'approved';
  const isRestrictedInvitedCreator = currentUser?.user_type === 'creator'
    && !isApprovedCreator
    && (
      currentUser?.creator_profile?.has_invited_access
      || invitedOfferIds.length > 0
      || currentUser?.creator_profile?.invited_collab
    );
  const [payoutCountry, setPayoutCountry] = useState('');
  const [payoutEntityType, setPayoutEntityType] = useState('individual');
  const [withdrawalQuote, setWithdrawalQuote] = useState(null);
  const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [quotingWithdrawal, setQuotingWithdrawal] = useState(false);
  const [confirmingWithdrawal, setConfirmingWithdrawal] = useState(false);
  const [retryingWithdrawal, setRetryingWithdrawal] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');
  const stripeRedirectHandled = useRef(false);

  const loadWallet = useCallback(async () => {
    try {
      const response = await getCreatorWallet();
      setWallet(response.data);
      setPayoutCountry(response.data.payout_country || response.data.payout_country_options?.[0]?.code || '');
      setPayoutEntityType(response.data.payout_entity_type || 'individual');
      return response.data;
    } catch (error) {
      toast.error(t('wallet.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    document.title = `${t('wallet.title')} | FOTH`;
    loadWallet();
  }, [loadWallet, t]);

  useEffect(() => {
    const stripeAction = new URLSearchParams(window.location.search).get('stripe');
    if (stripeAction !== 'refresh' || stripeRedirectHandled.current) return;

    stripeRedirectHandled.current = true;
    setStarting(true);
    startCreatorGlobalPayoutsOnboarding()
      .then((response) => window.location.assign(response.data.onboarding_url))
      .catch(async (error) => {
        toast.error(onboardingErrorMessage(error, t));
        await loadWallet();
        setStarting(false);
      });
  }, [loadWallet, t]);

  const status = useMemo(() => {
      const configs = {
      ready: { icon: CheckCircle2, title: t('wallet.onboarding.ready'), copy: t('wallet.onboarding.ready_help') },
      under_review: { icon: Clock3, title: t('wallet.onboarding.review'), copy: t('wallet.onboarding.review_help') },
      action_required: { icon: Landmark, title: t('wallet.onboarding.required'), copy: t('wallet.onboarding.required_help') },
      restricted: { icon: Landmark, title: t('wallet.onboarding.restricted'), copy: t('wallet.onboarding.restricted_help') },
      onboarding: { icon: Landmark, title: t('wallet.onboarding.incomplete'), copy: t('wallet.onboarding.incomplete_help') },
      error: { icon: Landmark, title: t('wallet.onboarding.restricted'), copy: t('wallet.onboarding.restricted_help') },
      not_started: { icon: Landmark, title: t('wallet.onboarding.start'), copy: t('wallet.onboarding.start_help') }
    };
    const setupStatus = wallet?.payout_setup_status || wallet?.global_payouts_status;
    if (setupStatus === 'ready' && !wallet?.payout_setup_ready) return configs.action_required;
    return configs[setupStatus] || configs.not_started;
  }, [t, wallet]);

  useEffect(() => {
    if (wallet?.can_withdraw) track('withdrawal_cta_viewed', { status: 'ready' });
  }, [wallet?.can_withdraw]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && withdrawalModalOpen && !confirmingWithdrawal) {
        setWithdrawalQuote(null);
        setWithdrawalModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [confirmingWithdrawal, withdrawalModalOpen]);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const hasRecipient = Boolean(wallet?.global_payouts_recipient_id);
      track(hasRecipient ? 'payout_setup_resumed' : 'payout_setup_started', { status: payoutStatus });
      const response = await startCreatorGlobalPayoutsOnboarding(
        hasRecipient ? {} : { payout_country: payoutCountry, payout_entity_type: payoutEntityType }
      );
      window.location.assign(response.data.onboarding_url);
    } catch (error) {
      toast.error(onboardingErrorMessage(error, t));
      // Stripe may have created the recipient before Account Link creation failed.
      await loadWallet();
      setStarting(false);
    }
  };

  const requestWithdrawal = async (amountOverride) => {
    setQuotingWithdrawal(true);
    try {
      track('withdrawal_started', { status: 'ready' });
      const amount = typeof amountOverride === 'string' || typeof amountOverride === 'number'
        ? amountOverride
        : (withdrawalAmount || wallet?.available_earnings);
      const response = await quoteCreatorWithdrawal(amount);
      track('withdrawal_quote_created', { method: response.data.payout_method_type });
      if (response.data.high_fee_warning) track('withdrawal_high_fee_warning_viewed', { method: response.data.payout_method_type });
      setWithdrawalQuote(response.data);
    } catch (error) {
      toast.error(withdrawalErrorMessage(error, t));
    } finally {
      setQuotingWithdrawal(false);
    }
  };

  const confirmWithdrawal = async () => {
    setConfirmingWithdrawal(true);
    try {
      const response = await confirmCreatorWithdrawal(withdrawalQuote.id);
      setWithdrawalQuote(null);
      setWithdrawalModalOpen(false);
      if (withdrawalQuote.high_fee_warning) track('withdrawal_anyway_selected', { method: withdrawalQuote.payout_method_type });
      track('withdrawal_confirmed', { status: response.data.status });
      toast.success(response.data.status === 'processing' ? t('wallet.withdrawal.processing') : t('wallet.withdrawal.confirmed'));
      await loadWallet();
    } catch (error) {
      toast.error(withdrawalErrorMessage(error, t));
      const refreshed = await loadWallet();
      setWithdrawalQuote(null);
      const failedWithdrawal = refreshed?.withdrawals?.find((item) => item.id === withdrawalQuote.id);
      const safeToRequote = refreshed?.can_withdraw
        && ['failed', 'returned', 'canceled'].includes(failedWithdrawal?.status);
      // Requote only an authoritative failure with available earnings.
      setWithdrawalModalOpen(Boolean(safeToRequote));
      if (safeToRequote) setWithdrawalAmount(String(refreshed.available_earnings ?? refreshed.available_balance ?? ''));
    } finally {
      setConfirmingWithdrawal(false);
    }
  };

  const retryWithdrawal = async (withdrawalId) => {
    setRetryingWithdrawal(withdrawalId);
    try {
      const response = await retryCreatorWithdrawal(withdrawalId);
      setWithdrawalQuote(response.data);
      setWithdrawalModalOpen(true);
      await loadWallet();
    } catch (error) {
      toast.error(withdrawalErrorMessage(error, t));
    } finally {
      setRetryingWithdrawal(null);
    }
  };

  if (loading) return <main className={styles.loading}>{t('wallet.loading')}</main>;

  const StatusIcon = status.icon;
  const availableAmount = Number(wallet.available_earnings ?? wallet.available_balance ?? 0);
  const pendingAmount = Number(wallet.pending_earnings ?? wallet.pending_balance ?? 0);
  const activeWithdrawal = (wallet.withdrawals || []).find((withdrawal) => (
    ['confirmed', 'waiting_for_funding', 'ready_to_send', 'processing'].includes(withdrawal.status)
  ));
  const pendingReleaseLabel = wallet.pending_release_at
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(wallet.pending_release_at))
    : null;
  const pendingReleaseCopy = pendingReleaseLabel
    ? t('wallet.earnings.pending_help', { date: pendingReleaseLabel })
    : t('wallet.earnings.pending_help_no_date');
  const withdrawalBlockCopy = wallet.withdrawal_block_reason === 'PAYOUT_RELEASE_PENDING'
    ? pendingReleaseCopy
    : wallet.withdrawal_block_reason === 'WITHDRAWAL_ALREADY_PROCESSING'
      ? activeWithdrawal?.status === 'waiting_for_funding'
        ? t('wallet.withdrawal.waiting_for_funding')
        : t('wallet.withdrawal.already_processing')
      : wallet.withdrawal_block_reason === 'PAYOUT_UNDER_REVIEW'
        ? t('wallet.withdrawal.under_review')
        : wallet.withdrawal_block_reason === 'PAYOUT_METHOD_MISSING'
          ? t('wallet.withdrawal.method_missing')
          : wallet.withdrawal_block_reason === 'PAYOUT_SETUP_REQUIRED'
            ? t('wallet.withdrawal.setup_required')
            : t('wallet.withdrawal.no_available');
  const payoutReady = Boolean(wallet.payout_setup_ready);
  const payoutStatus = wallet.payout_setup_status || wallet.global_payouts_status;
  const safeDestination = wallet.payout_method_safe_display;
  const destinationCountryName = wallet.payout_country_options?.find((option) => option.code === wallet.payout_country)?.name || wallet.payout_country;
  const destinationLabel = safeDestination?.last4
    ? `${safeDestination.type || 'Bank'} ••••${safeDestination.last4}`
    : null;
  const showSetupCta = wallet.global_payouts_enabled && !payoutReady && ['not_started', 'onboarding', 'action_required', 'restricted', 'error'].includes(payoutStatus);
  const openWithdrawalModal = () => {
    const fullBalance = String(wallet.available_earnings ?? wallet.available_balance ?? '');
    setWithdrawalAmount(fullBalance);
    setWithdrawalModalOpen(true);
    requestWithdrawal(fullBalance);
  };
  const feeItems = withdrawalQuote?.stripe_quote_fee_breakdown?.length
    ? withdrawalQuote.stripe_quote_fee_breakdown
    : [
      { type: 'wire_payout_fee', amount: withdrawalQuote?.estimated_base_fee },
      { type: 'cross_border_payout_fee', amount: withdrawalQuote?.estimated_cross_border_fee },
      { type: 'foreign_exchange_fee', amount: withdrawalQuote?.estimated_fx_fee },
    ].filter((fee) => Number(fee.amount || 0) > 0);
  const activityItems = [
    ...(wallet.entries || []).map((entry) => ({
      id: `entry-${entry.id}`,
      type: 'income',
      title: entry.collab_title,
      date: entry.created_at,
      status: t(`wallet.status.${entry.status}`),
      amount: money(entry.amount, wallet.currency),
    })),
    ...(wallet.withdrawals || []).map((withdrawal) => ({
      id: `withdrawal-${withdrawal.id}`,
      type: 'withdrawal',
      title: withdrawal.payout_method_safe_display?.last4
        ? `${withdrawal.payout_method_safe_display.type || 'Bank'} ••••${withdrawal.payout_method_safe_display.last4}`
        : `${withdrawal.payout_method_type} · ${withdrawal.payout_country}`,
      date: withdrawal.created_at,
      status: t(`wallet.withdrawal_status.${withdrawal.status}`, withdrawal.status),
      amount: money(withdrawal.net_amount ?? withdrawal.net_payout_amount, wallet.currency),
    })),
  ].sort((first, second) => new Date(second.date) - new Date(first.date));
  const visibleActivityItems = activityItems.filter((item) => activityFilter === 'all' || item.type === activityFilter);
  return (
    <main className={styles.page}>
      {isRestrictedInvitedCreator && (
        <div className={styles.invitedNav}>
          <Link to="/creator/invited" className={styles.navTab}>
            <BriefcaseBusiness size={16} />
            <span>{t('invited_dashboard.tab_collabs', 'Mis Collabs')}</span>
          </Link>
          <Link to="/creator/wallet" className={`${styles.navTab} ${styles.active}`}>
            <Wallet size={16} />
            <span>{t('invited_dashboard.tab_earnings', 'Ganancias')}</span>
          </Link>
        </div>
      )}
      <header className={styles.hero}>
        <div className={styles.heroCopy}>
          <span className={styles.eyebrow}>{t('wallet.eyebrow')}</span>
          <h1>{t('wallet.title')}</h1>
          <p>{t('wallet.subtitle')}</p>
        </div>
        <div className={styles.balanceCard}>
          <div className={styles.balanceTopline}>
            <span>{t('wallet.available')}</span>
            <span className={styles.currencyBadge}>{wallet.currency}</span>
          </div>
          <strong className={styles.balanceAmount}>{money(availableAmount, wallet.currency)}</strong>
          <div className={styles.balanceMeta}>
            <span className={styles.statusDot} aria-hidden="true" />
            <span>{payoutReady ? t('wallet.onboarding.ready') : t('wallet.onboarding.required')}</span>
          </div>
          <div className={styles.balanceActions}>
            <span>{wallet.withdrawal_block_reason === 'PAYOUT_RELEASE_PENDING'
              ? pendingReleaseCopy
              : payoutReady ? t('wallet.onboarding.ready_help') : t('wallet.earnings.action_help')}</span>
            {wallet.can_withdraw && availableAmount > 0 ? (
              <Button onClick={openWithdrawalModal} disabled={quotingWithdrawal} loading={quotingWithdrawal}>
                {t('wallet.withdraw')} <ArrowUpRight size={17} />
              </Button>
            ) : (
              <span className={styles.balanceUnavailable}>{withdrawalBlockCopy}</span>
            )}
          </div>
        </div>
      </header>

      <section className={styles.statsGrid} aria-label={t('wallet.summary_label')}>
        <div className={styles.statCard}><Clock3 size={19} /><div><span>{t('wallet.pending')}</span><strong>{money(pendingAmount, wallet.currency)}</strong></div></div>
        <div className={styles.statCard}><CircleDollarSign size={19} /><div><span>{t('wallet.paid_collabs')}</span><strong>{wallet.paid_collab_count || 0} · {money(wallet.paid_collab_total, wallet.currency)}</strong></div></div>
        <div className={styles.statCard}><Sparkles size={19} /><div><span>{t('wallet.combined_value')}</span><strong>{money(wallet.combined_collab_value, wallet.currency)}</strong></div></div>
      </section>

      {pendingAmount > 0 && (
        <section className={styles.earningsAlert} role="status">
          <div>
            <strong>{t('wallet.earnings.pending_title')}</strong>
            <p>{pendingReleaseCopy}</p>
          </div>
        </section>
      )}

      {activeWithdrawal?.status === 'waiting_for_funding' && (
        <section className={styles.earningsAlert} role="status">
          <div>
            <strong>{t('wallet.withdrawal.waiting_for_funding_title')}</strong>
            <p>{t('wallet.withdrawal.waiting_for_funding')}</p>
          </div>
        </section>
      )}

      {availableAmount > 0 && !payoutReady && wallet.global_payouts_enabled && (
        <section className={styles.earningsAlert} role="status">
          <div>
            <strong>{payoutStatus === 'under_review' ? t('wallet.earnings.review_title') : t('wallet.earnings.action_title')}</strong>
            <p>{payoutStatus === 'under_review' ? t('wallet.earnings.review_help') : t('wallet.earnings.action_help')}</p>
          </div>
          {showSetupCta && <Button onClick={startOnboarding} disabled={starting}>{t('wallet.earnings.setup_cta')}</Button>}
        </section>
      )}

      <div className={styles.contentGrid}>
        <section className={`${styles.panel} ${styles.activity}`}>
          <div className={styles.panelHeader}>
            <div><span className={styles.sectionKicker}>{t('wallet.activity.eyebrow')}</span><h2>{t('wallet.activity.title')}</h2></div>
            <span className={styles.activityCount}>{activityItems.length} {t('wallet.activity.title').toLowerCase()}</span>
          </div>
          <div className={styles.filters} role="group" aria-label={t('wallet.activity.title')}>
            {[
              ['all', t('wallet.filters.all', 'Todos')],
              ['income', t('wallet.filters.income', 'Ingresos')],
              ['withdrawal', t('wallet.filters.withdrawal', 'Retiros')],
            ].map(([filter, label]) => (
              <button key={filter} type="button" className={`${styles.filterButton} ${activityFilter === filter ? styles.active : ''}`} onClick={() => setActivityFilter(filter)}>
                {label}
              </button>
            ))}
          </div>
          {visibleActivityItems.length === 0 ? (
            <div className={styles.empty}><Wallet size={28} /><h3>{t('wallet.activity.empty')}</h3><p>{t('wallet.activity.empty_help')}</p></div>
          ) : (
            <div className={styles.transactions}>
              {visibleActivityItems.map((item) => (
                <div className={styles.transaction} key={item.id}>
                  <div className={`${styles.activityIcon} ${item.type === 'withdrawal' ? styles.withdrawalIcon : ''}`}>
                    {item.type === 'withdrawal' ? <ArrowDownLeft size={17} /> : <ArrowUpRight size={17} />}
                  </div>
                  <div className={styles.transactionMain}><strong>{item.title}</strong><span>{new Date(item.date).toLocaleDateString()}</span></div>
                  <div className={styles.transactionValue}><span className={styles.entryStatus}>{item.status}</span><strong>{item.type === 'withdrawal' ? '-' : '+'}{item.amount}</strong></div>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className={styles.sideStack}>
          <section className={`${styles.panel} ${styles.payoutCard}`}>
            <span className={styles.sectionKicker}>{t('wallet.payouts')}</span>
            <div className={styles.payoutHeading}>
              <div className={styles.statusIcon}><StatusIcon size={21} /></div>
              <div className={styles.statusCopy}><h2>{status.title}</h2><p>{status.copy}</p></div>
            </div>
            {payoutReady && destinationLabel && <div className={styles.destination}>{destinationLabel}</div>}
            {(wallet.status_stale || wallet.global_payouts_status_stale) && <p className={styles.staleNotice} role="status">{t('wallet.onboarding.stale')}</p>}
            {!wallet.global_payouts_enabled && <p className={styles.disabledNotice}>{t('wallet.onboarding.disabled')}</p>}
            {!wallet.global_payouts_recipient_id && wallet.global_payouts_enabled && wallet.global_payouts_status === 'not_started' && (
              <div className={styles.setupFields}>
                <label>
                  <span>{t('wallet.onboarding.payout_country')}</span>
                  <select value={payoutCountry} onChange={(event) => setPayoutCountry(event.target.value)}>
                    {wallet.payout_country_options.map((option) => <option key={option.code} value={option.code}>{option.name}</option>)}
                  </select>
                  <small>{t('wallet.onboarding.payout_country_help')}</small>
                </label>
                <label>
                  <span>{t('wallet.onboarding.account_type')}</span>
                  <select value={payoutEntityType} onChange={(event) => setPayoutEntityType(event.target.value)}>
                    <option value="individual">{t('wallet.onboarding.individual')}</option>
                    <option value="company">{t('wallet.onboarding.company')}</option>
                  </select>
                </label>
              </div>
            )}
            {showSetupCta && <Button fullWidth onClick={startOnboarding} disabled={starting}>{starting ? t('wallet.onboarding.opening') : wallet.global_payouts_recipient_id ? t('wallet.onboarding.continue') : t('wallet.onboarding.action')} <ArrowUpRight size={17} /></Button>}
            {wallet.global_payouts_enabled && payoutReady && <Button variant="outline" fullWidth onClick={startOnboarding} disabled={starting}>{starting ? t('wallet.onboarding.opening') : t('wallet.onboarding.manage')} <ArrowUpRight size={17} /></Button>}
          </section>
          <section className={styles.creatorNote}>
            <span className={styles.sectionKicker}>{t('wallet.collab_summary_label')}</span>
            <h3>{t('wallet.creator_note.title', 'Tu trabajo tiene valor.')}</h3>
            <p>{t('wallet.creator_note.copy', 'Has generado ingresos y valor de marca con tus colaboraciones. Sigue creando; tu próxima oportunidad puede estar a un clic.')}</p>
            <Link to="/creator/feed" className={styles.noteLink}>{t('wallet.creator_note.cta', 'Explorar nuevas collabs')} <ChevronRight size={16} /></Link>
          </section>
        </aside>
      </div>

      {withdrawalModalOpen && (
        <div className={styles.withdrawalBackdrop} role="presentation" onClick={(event) => { if (event.target === event.currentTarget && !confirmingWithdrawal) { setWithdrawalQuote(null); setWithdrawalModalOpen(false); } }}>
          <section className={styles.withdrawalPanel} role="dialog" aria-modal="true" aria-labelledby="withdrawal-title">
            <div className={styles.modalHeader}><div><span className={styles.sectionKicker}>{t('wallet.withdrawal.title')}</span><h2 id="withdrawal-title">{t('wallet.withdrawal.confirm_action')}</h2></div><button className={styles.closeButton} type="button" onClick={() => { setWithdrawalQuote(null); setWithdrawalModalOpen(false); }} disabled={confirmingWithdrawal} aria-label={t('common.close', 'Cerrar')}><X size={18} /></button></div>
            <p className={styles.modalCopy}>{t('wallet.withdrawal.secure_destination')}</p>
            <div className={styles.quoteGrid}>
              <div><span>{t('wallet.withdrawal.available')}</span><strong>{money(availableAmount, wallet.currency)}</strong></div>
              <label className={styles.amountField}><span>{t('wallet.withdrawal.amount')}</span><input autoFocus type="number" min={wallet.payout_country === 'PA' ? '50' : '0.01'} step="0.01" value={withdrawalAmount} onChange={(event) => { setWithdrawalAmount(event.target.value); setWithdrawalQuote(null); }} /></label>
              <div><span>{t('wallet.withdrawal.destination')}</span><strong>{destinationLabel || t('wallet.withdrawal.secure_destination')}</strong></div>
              <div><span>{t('wallet.withdrawal.country')}</span><strong>{destinationCountryName}</strong></div>
              <div><span>{t('wallet.withdrawal.rail')}</span><strong>{wallet.payout_method_type || 'WIRE'}</strong></div>
            </div>
            {withdrawalQuote && <>
              <div className={styles.quoteBreakdown}>
                {withdrawalQuote.fees_paid_by_business ? <p>{t('wallet.withdrawal.business_covers_fees')}</p> : <>
                  <strong>{t('wallet.withdrawal.total_fee')}</strong>
                  {feeItems.map((fee) => <div key={fee.type}><span>{fee.type.replaceAll('_', ' ')}</span><strong>-{money(fee.amount, wallet.currency)}</strong></div>)}
                  <div className={styles.quoteTotal}><span>{t('wallet.withdrawal.total_fee')}</span><strong>-{money(withdrawalQuote.estimated_payout_fee, wallet.currency)}</strong></div>
                </>}
                <div className={styles.quoteTotal}><span>{t('wallet.withdrawal.receive')}</span><strong>{money(withdrawalQuote.estimated_amount_to_receive, withdrawalQuote.destination_currency || wallet.currency)}</strong></div>
              </div>
              {!withdrawalQuote.fees_paid_by_business && <p className={styles.disclosure}>{t('wallet.withdrawal.disclosure', 'Stripe charges fees for sending payouts to your bank. The exact fees shown here are calculated for this transfer before you confirm.')}</p>}
            </>}
            {withdrawalQuote?.high_fee_warning && <div className={styles.highFeeWarning} role="alert"><strong>{t('wallet.withdrawal.warning_title')}</strong><p>{withdrawalQuote.high_fee_warning_message || t('wallet.withdrawal.warning_body')}</p></div>}
            <div className={styles.withdrawalActions}>
              <Button variant="outline" onClick={() => { track('withdrawal_wait_selected', { method: wallet.payout_method_type }); setWithdrawalQuote(null); setWithdrawalModalOpen(false); }} disabled={confirmingWithdrawal}>{t('wallet.withdrawal.wait')}</Button>
              {withdrawalQuote ? <Button onClick={confirmWithdrawal} disabled={confirmingWithdrawal} loading={confirmingWithdrawal}>{t('wallet.withdrawal.confirm_action')}</Button> : <Button onClick={() => requestWithdrawal()} disabled={quotingWithdrawal} loading={quotingWithdrawal}>{t('wallet.withdrawal.retry')}</Button>}
            </div>
          </section>
        </div>
      )}

      <section className={`${styles.panel} ${styles.historyPanel}`}>
        <div className={styles.panelHeader}><div><span className={styles.sectionKicker}>{t('wallet.history.title')}</span><h2>{t('wallet.history.title')}</h2></div></div>
        {!wallet.withdrawals?.length ? (
          <div className={styles.empty}><Wallet size={28} /><h3>{t('wallet.history.empty')}</h3></div>
        ) : (
          <div className={styles.transactions}>
            {wallet.withdrawals.map((withdrawal) => (
              <div className={styles.transaction} key={withdrawal.id}>
                <div className={`${styles.activityIcon} ${styles.withdrawalIcon}`}><ArrowDownLeft size={17} /></div>
                <div className={styles.transactionMain}><strong>{withdrawal.payout_method_safe_display?.last4 ? `${withdrawal.payout_method_safe_display.type || 'Bank'} ••••${withdrawal.payout_method_safe_display.last4}` : `${withdrawal.payout_method_type} · ${withdrawal.payout_country}`}</strong><span>{new Date(withdrawal.created_at).toLocaleDateString()}</span></div>
                <span className={styles.entryStatus}>{t(`wallet.withdrawal_status.${withdrawal.status}`, withdrawal.status)}</span>
                <div className={styles.transactionValue}>
                  <strong>{money(withdrawal.net_amount ?? withdrawal.net_payout_amount, wallet.currency)}</strong>
                  <small>{t('wallet.history.gross')}: {money(withdrawal.gross_amount ?? withdrawal.gross_withdrawal_amount, withdrawal.source_currency || wallet.currency)} · {withdrawal.fees_paid_by_business ? t('wallet.withdrawal.business_covers_fees') : `${t('wallet.history.fees')}: -${money(withdrawal.payout_fees ?? withdrawal.estimated_payout_fee, wallet.currency)}`}</small>
                  {withdrawal.status === 'failed' && <small className={styles.recoveryMessage}>{t('wallet.withdrawal.failed_help')}</small>}
                  {withdrawal.status === 'returned' && <small className={styles.recoveryMessage}>{t('wallet.withdrawal.returned_help')}</small>}
                  {['failed', 'returned'].includes(withdrawal.status) && (
                    <Button
                      variant="outline"
                      onClick={() => retryWithdrawal(withdrawal.id)}
                      disabled={retryingWithdrawal === withdrawal.id}
                      loading={retryingWithdrawal === withdrawal.id}
                    >
                      {t('wallet.withdrawal.retry')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default CreatorWallet;
